import { zodResolver } from '@hookform/resolvers/zod';
import {
  BaseResponseSchema,
  Maybe,
  getRequiredMessage,
} from '@monetize/types/common';
import { isValidDate } from '@monetize/utils/core';
import { isDate, parseISO } from 'date-fns';
import isNaN from 'lodash/isNaN';
import isNull from 'lodash/isNull';
import isNumber from 'lodash/isNumber';
import { ResolverOptions } from 'react-hook-form';
import { z } from 'zod';
import { AccountRespSchema } from './account.types';
import { AddressSchema } from './address.types';
import { BillGroupSummarySchema } from './billGroup.types';
import { ContactRespSchema, QuoteContacts } from './contact.types';
import { CustomFieldRecordSchema } from './customFields.types';
import { QuoteItemCustomPriceSchema } from './customPrice.types';
import { DiscountSchema, QuoteItemDiscountSchema } from './discount.types';
import {
  AmountUnitTypeEnum,
  ApprovalStatusEnumZ,
  CollaborationAccessEnumZ,
  CollaborationAccessLevelEnumZ,
  ContractEndActionEnum,
  ContractEndActionEnumZ,
  DiscountApplicationMethodEnumZ,
  DiscountDurationEnumZ,
  DiscountLifeCyclePolicyEnum,
  DiscountLifeCyclePolicyEnumZ,
  DiscountStatusEnumZ,
  DiscountTypeEnumZ,
  NetTermsEnumZ,
  NewQuoteTypeEnum,
  NewQuoteTypeEnumZ,
  OpportunityStatusEnumZ,
  PriceModelEnum,
  ProductTypeEnum,
  ProductTypeEnumZ,
  QuoteCancelationReasonEnumZ,
  QuoteItemAmendmentStatusEnumZ,
  QuoteSettingsDefaultAddressSourceEnumZ,
  QuoteStatusEnumZ,
  QuoteTypeEnum,
  QuoteTypeEnumZ,
  RateBillingFrequencyEnumZ,
  RenewalTermLengthEnum,
  RenewalTermLengthEnumZ,
  SigningOrderEnum,
  SigningOrderEnumZ,
} from './enums.types';
import { LegalEntitySnapshot } from './legalEntity.types';
import { OfferingResSchema, OfferingTypesEnumZ } from './offering.types';
import { PaymentMethodSummaryResponseSchema } from './paymentMethod.types';
import { PriceUpliftConfigResSchema } from './priceUpliftConfig.types';
import {
  ProductQuantityDisplayTypeEnum,
  ProductQuantityDisplayTypeEnumZ,
  ProductSchema,
  ProrationPolicyEnum,
} from './product.types';
import { QuoteStartDateSourceEnumZ } from './quoteSettings.types';
import {
  IRateResSchema,
  PriceDisplayEnumZ,
  RateResBaseSchema,
} from './rate.types';
import {
  ApplyRecordAutomationRulesResponseSchema,
  RuleValidationErrorLevelEnumZ,
  RuleValidationErrorLocationEnumZ,
  RuleValidationFieldSchema,
} from './rules.types';
import {
  DocumentParentEnumZ,
  QuoteDocumentConfigPositionEnumZ,
  QuoteDocumentConfigSummarySchema,
} from './storage.types';

export interface QuoteItemDiscount {
  createDate: string;
  createdBy: string;
  discountAmount: number;
  discountAmountOrPercent: number;
  discountId: string;
  discountName: string;
  discountType: AmountUnitTypeEnum;
  lastModifiedBy: string;
  modifyDate: string;
}

export const QuoteItemSchema = z.object({
  id: z.string().optional().nullable(),
  quoteOffering: z.any().optional().nullable(),
  productId: z.string().nullish(),
  quoteOfferingId: z.string().nullish(),
  name: z.string().nullish(),
  sku: z.string().optional().nullable(),
  subscriptionItemId: z.string().optional().nullable(),
  endDate: z.string().optional().nullable(),
  description: z.string().optional().nullable(),
  isAddon: z.boolean().optional().nullable(),
  addonId: z.string().optional().nullable(),
  quantity: z
    .number()
    .refine(
      (val) => val !== null && isFinite(val),
      getRequiredMessage('Quantity'),
    )
    .transform((val) => Number(val)),
  incrementalQuantity: z.number().nullish(),
  debit: z.number().optional().nullable(),
  creditProrationMultiplier: z.number().optional().nullable(),
  credit: z.number().optional().nullable(),
  amount: z.number().optional(),
  amountWithoutDiscount: z.number().optional(),
  customDiscountId: z.string().optional().nullable(),
  customDiscountType: DiscountTypeEnumZ.optional().nullable(),
  customDiscountAmountOrPercent: z
    .union([z.number(), z.string()])
    .nullable()
    .transform((val) => (val === null ? null : Number(val))),
  customDiscountName: z.string().nullish(),
  customDiscountDescription: z.string().nullish(),
  customDiscountExcludeFromRenewal: z.boolean().nullish(),
  customDiscountLifecyclePolicies: z
    .array(DiscountLifeCyclePolicyEnumZ)
    .nullish(),
  discounts: z.array(QuoteItemDiscountSchema).optional(),
  discountAmount: z.number().nullish(),
  discountPercent: z.number().nullish(),
  approved: z.boolean().optional().nullable(),
  amendmentStatus: z.string().optional().nullable(),
  previousItemId: z.string().optional().nullable(),
  previousRateId: z.string().optional().nullable(),
  previousAmount: z.number().optional().nullable(),
  previousQuantity: z.number().optional().nullable(),
  previousDiscountId: z.string().optional().nullable(),
  previousDiscountType: DiscountTypeEnumZ.optional().nullable(),
  previousDiscountAmountOrPercent: z.number().optional().nullable(),
  productName: z.string().nullish(),
  productType: z.string().nullish(),
  unitPrice: z.number().nullish(),
  unitPriceAfterDiscount: z.number().nullish(),
});

export type IQuoteItemSchema = z.infer<typeof QuoteItemSchema>;
export type IQuoteItem = IQuoteItemSchema;
export interface ICurrency {
  id: number;
  sortName: string;
  fullName: string;
}

export enum QuoteAmendmentVersionEnum {
  v1 = 'amendment_v1',
  v2 = 'amendment_v2',
}
export const QuoteAmendmentVersionEnumZ = z.enum(QuoteAmendmentVersionEnum);

export const QuoteItemOptionsSchema = z.object({
  showPrices: z.boolean().nullish(),
  displayUnitPriceFrequency: z.number().min(1).max(60).nullish(),
  priceDisplay: PriceDisplayEnumZ.nullish(),
  priceDisplayMode: z.string().nullish(),
  displayedPriceIds: z.array(z.string()).nullish(),
});
export type IQuoteItemOptionsSchema = z.infer<typeof QuoteItemOptionsSchema>;

export const QuoteItemNoteSchema = z.object({
  notes: z
    .string()
    .max(2000, {
      error: 'Notes cannot exceed 2000 characters',
    })
    .optional(),
});
export type QuoteItemNote = z.infer<typeof QuoteItemNoteSchema>;

export const QuoteOfferingSchema = z.object({
  id: z.string().optional(),
  name: z.string().nullish(),
  offeringId: z.string().nonempty({
    error: 'offeringId is required!',
  }),
  rateId: z
    .string()
    .nonempty({
      error: 'rateId is required!',
    })
    .nullish(),
  productId: z.string().nullish(),
  items: z.array(QuoteItemSchema),
  subscriptionId: z.string().nullish(),
  description: z.string().nullish(),
  debit: z.number().optional(),
  creditProrationMultiplier: z.number().optional(),
  credit: z.number().optional(),
  amount: z.number().optional(),
  amountWithoutDiscount: z.number().optional(),
  discounts: z.array(DiscountSchema).optional(),
  discountIds: z.array(z.string()).optional().nullable(),
  discountType: DiscountTypeEnumZ.optional(),
  discountAmountOrPercent: z.number().optional(),
  discount: z.number().optional(),
  discountAmount: z.number().optional(),
  discountPercent: z.number().optional(),
  previousRateId: z.string().nullish(),
  previousAmount: z.number().nullish(),
  previousDiscountId: z.string().optional(),
  previousDiscountType: DiscountTypeEnumZ.optional(),
  previousDiscountAmountOrPercent: z.number().optional(),
  products: z.array(ProductSchema).optional(),
  rates: z.array(RateResBaseSchema).optional(),
  amendmentStatus: z.string().optional().nullable(),
  type: OfferingTypesEnumZ.optional(),
  startDate: z.string(),
  endDate: z.string(),
  parentQuoteOfferingId: z.string().nullish(),
});
export type IQuoteOffering = z.infer<typeof QuoteOfferingSchema>;

export const OpportunitySchema = z.object({
  id: z.string().nonempty({
    error: 'opportunityId is required!',
  }),
  name: z.string().optional().nullable(),
  customId: z.string().optional().nullable(),
  primaryQuoteId: z.string().optional().nullable(),
});
export type IOpportunitySchema = z.infer<typeof OpportunitySchema>;

// from salesforce service route '/:opportunityId/quote/link'
export interface OpportunityResponseSchema {
  opportunityId: string;
  customOpportunityId: string;
  opportunityName: string;
  quoteId: string;
}

export const QuoteContractRenewalTerms = z
  .object({
    contractEndAction: ContractEndActionEnumZ,
    autoRenewalNoticePeriod: z
      .number()
      .refine(
        (val) => val !== null && val !== undefined && !isNaN(val),
        getRequiredMessage('Notice Period'),
      )
      .transform((val) => Number(val))
      .refine((val) => val <= 100, 'Must be less than or equal to 100')
      .nullish(),
    renewalTermLength: z
      .object({
        type: RenewalTermLengthEnumZ,
        months: z.number().nullish(),
      })
      .nullish()
      .superRefine((data, ctx) => {
        if (data?.type === RenewalTermLengthEnum.FixedMonths && !data?.months) {
          ctx.addIssue({
            code: 'custom',
            message: getRequiredMessage('Months'),
            path: ['months'],
          });
        }

        if (
          data?.type === RenewalTermLengthEnum.FixedMonths &&
          data.months &&
          (data.months > 120 || data.months < 1)
        ) {
          ctx.addIssue({
            code: 'custom',
            message: 'The Custom Length must be between 1 to 120',
            path: ['months'],
          });
        }
      }),
  })
  .superRefine((data, ctx) => {
    if (
      data.contractEndAction === ContractEndActionEnum.RENEW &&
      data.autoRenewalNoticePeriod == null
    ) {
      ctx.addIssue({
        code: 'custom',
        message: getRequiredMessage('Notice Period'),
        path: ['autoRenewalNoticePeriod'],
      });
    }
  });

export type IQuoteContractRenewalTerms = z.infer<
  typeof QuoteContractRenewalTerms
>;

export const ContractAnchorBillingDaySchema = z
  .object({
    day: z.number().min(1).max(31).nullable(),
    month: z.number().min(1).max(12).nullable(),
  })
  .superRefine((val, ctx) => {
    const { day, month } = val;

    if (isNull(day) && !isNull(month)) {
      ctx.addIssue({
        code: 'custom',
        message: 'A day must be set.',
        path: ['day'],
      });
    }

    if (!isNull(day) && isNull(month)) {
      ctx.addIssue({
        code: 'custom',
        message: 'A month must be set.',
        path: ['month'],
      });
    }
  });

export type IContractAnchorBillingDay = z.infer<
  typeof ContractAnchorBillingDaySchema
>;

// Quote Request schema will be used for both PUT/POST since they are identical
// !!NOTE!! IF any fields are added here that don't exist on QuoteRespSchema, update getQuoteRequestFromQuoteResponse()
// from POST /api/quote/
// from PUT /api/quote/{quoteId}
export const QuoteRequestSchema = z.object({
  customId: z.string().nullish(),
  description: z
    .string()
    .min(1, { error: 'Quote Name is required' })
    .max(255, { error: 'Quote Name must contain at most 255 character(s)' }),
  opportunityId: z.string().nullish(),
  accountId: z.string().nullish(),
  linkedAccountId: z.string().nullish(),
  billingAccountId: z.string().nullish(),
  collaborationAccess: CollaborationAccessEnumZ,
  billGroupId: z.string().nullish(),
  expirationDate: z.string().nullish(),
  currency: z.string(),
  netTerms: NetTermsEnumZ,
  contractStartDate: z.string().nullish(),
  contractEndDate: z.string().nullish(),
  contractAmendmentDate: z.string().nullish(),
  contractLength: z.number().nullish(),
  contractTerms: z.string().nullish(),
  documentUrl: z.string().nullish(),
  /** @deprecated - will be replaced with contractRenewalTerms.contractEndAction */
  autoRenewContract: z.boolean(),
  requiresEsign: z.boolean(),
  legalEntityId: z.string(),
  customFields: CustomFieldRecordSchema,
  contractRenewalTerms: QuoteContractRenewalTerms.nullish(),
  migrated: z.boolean().nullish(),
  // FIXME: these are not actual properties on the quote request - BE ignores them if sent
  // but we have code that relies on them (incorrectly) in QuoteDocumentsBody
  documentLocation: z.string().nullish(),
  sowDocumentStorageId: z.string().nullish(),
  purchaseOrderNumber: z.string().max(255).nullish(),
  coverDocumentStorageId: z.string().nullish(),
  footerDocumentStorageId: z.string().nullish(),
  startDateSource: QuoteStartDateSourceEnumZ.nullish(),
  billingAnchor: ContractAnchorBillingDaySchema.nullable().transform((val) => {
    if (val?.day === null && val?.month === null) {
      return null;
    }
    return val;
  }),
  discountApplicationMethod: DiscountApplicationMethodEnumZ.nullish(),
});

export type IQuoteRequestSchema = z.infer<typeof QuoteRequestSchema>;

export const QuoteRequestSchemaExtended = QuoteRequestSchema.extend({
  account: z.union([AccountRespSchema, z.literal('')]),
  quoteType: z.union([QuoteTypeEnumZ, z.literal('')]),
  contractId: z.string(),
});
export type IQuoteRequestSchemaExtended = z.infer<
  typeof QuoteRequestSchemaExtended
>;
export type IQuoteRequestInputSchemaExtended = z.input<
  typeof QuoteRequestSchemaExtended
>;

export const getQuoteRequestSchema =
  (quote?: IQuoteRespSchema | null) =>
  (
    data: IQuoteRequestSchema,
    context: object | undefined,
    options: ResolverOptions<IQuoteRequestSchema>,
  ) => {
    const modifiedReqSchema = QuoteRequestSchema.superRefine(
      ({ contractLength }, ctx) => {
        if (
          quote?.type === QuoteTypeEnum.AMENDMENT &&
          quote?.amendmentVersion !== QuoteAmendmentVersionEnum.v2 &&
          Number(contractLength) < Number(quote?.previousContractLength)
        ) {
          ctx.addIssue({
            code: 'custom',
            message: `Contract length cannot be shortened in an amendment`,
            path: ['contractLength'],
          });
        }
      },
    );
    return zodResolver(modifiedReqSchema)(data, context, options);
  };

export const OpportunityQuoteRespSchema = z.object({
  id: z.string(),
  name: z.string(),
  modifyDate: z.string().optional(),
  type: QuoteTypeEnumZ,
  status: QuoteStatusEnumZ,
  total: z.number(),
  currency: z.string(),
  contractStartDate: z.string(),
  contractEndDate: z.string(),
  legalEntityId: z.string(),
});
export type IOpportunityQuote = z.infer<typeof OpportunityQuoteRespSchema>;

// from GET /api/quotes/{quoteId}:
export const GetQuoteOpportunitySchema = z.object({
  id: z.string(),
  accountName: z.string().nullish(),
  status: OpportunityStatusEnumZ,
  amount: z.number().nullish(),
  customId: z.string().nullish(),
  name: z.string(),
  owner: z.string(),
  primaryQuoteId: z.string().nullish(),
  quotes: OpportunityQuoteRespSchema.array().nullish(),
  dealRoomId: z.string().nullish(),
});
export type IGetQuoteOpportunitySchema = z.infer<
  typeof GetQuoteOpportunitySchema
>;

export const RevenueMetricsSchema = z.object({
  arr: z.number(),
  mrr: z.number(),
  incrementalMrr: z.number(),
  incrementalArr: z.number(),
  percentage: z.number(),
  previousArr: z.number(),
  previousMrr: z.number(),
});

export type IRevenueMetrics = z.infer<typeof RevenueMetricsSchema>;

export const RevenueMetricsQuoteItemSchema = RevenueMetricsSchema.pick({
  arr: true,
  mrr: true,
  incrementalMrr: true,
  incrementalArr: true,
});

export type IRevenueMetricsQuoteItem = z.infer<
  typeof RevenueMetricsQuoteItemSchema
>;

// from GET /api/quotes/{quoteId}:
export const QuoteItemRespSchema = BaseResponseSchema.extend({
  addonId: z.string().nullable(),
  amendmentStatus: QuoteItemAmendmentStatusEnumZ,
  // This is FE status to overwrite the BE amendmentStatus. This is used to provide user experience on amend V2
  amendmentStatusOverwrite: QuoteItemAmendmentStatusEnumZ.nullish(),
  amount: z.number(),
  amountWithoutDiscount: z.number(),
  credit: z.number(),
  creditProrationMultiplier: z.number(),
  customDiscountAmountOrPercent: z
    .union([z.number(), z.string()])
    .nullable()
    .transform((val) => (val === null ? null : Number(val))),
  customDiscountName: z.string().nullish(),
  customDiscountDescription: z.string().nullish(),
  customDiscountId: z.string().nullable(),
  customDiscountType: DiscountTypeEnumZ.nullable(),
  customDiscountExcludeFromRenewal: z.boolean().nullish(),
  customDiscountLifecyclePolicies: z
    .array(DiscountLifeCyclePolicyEnumZ)
    .nullish(),
  customId: z.string().nullable(),
  customPriceId: z.string().nullable(),
  customPrice: z
    .object({
      createdBy: z.string(),
      createDate: z.string(),
      lastModifiedBy: z.string(),
      modifyDate: z.string(),
      id: z.string(),
      amount: z.number(),
    })
    .nullable(),
  debit: z.number(),
  description: z.string().nullable(),
  discountAmount: z.number(),
  discountPercent: z.number(),
  discounts: z.array(QuoteItemDiscountSchema),
  endDate: z.string().nullable(),
  isAddon: z.boolean().nullable(),
  options: QuoteItemOptionsSchema.nullish(),
  previousAmount: z.number().nullable(),
  previousCustomDiscountAmountOrPercent: z.number().nullable(),
  previousCustomDiscountId: z.string().nullable(),
  previousCustomDiscountType: DiscountTypeEnumZ.nullable(),
  previousDiscountAmount: z.number().nullable(),
  previousDiscountPercent: z.number().nullable(),
  previousItemId: z.string().nullable(),
  previousQuantity: z.number().nullable(),
  productId: z.string(),
  productName: z.string(),
  productType: ProductTypeEnumZ,
  quantity: z.number(),
  quoteOfferingId: z.string(),
  revenueMetrics: RevenueMetricsQuoteItemSchema,
  sku: z.string().nullable(),
  subscriptionItemId: z.string().nullable(),
  unitPrice: z.number(),
  unitPriceAfterDiscount: z.number(),
  displayUnitPrice: z.number(),
  displayUnitPriceAfterDiscount: z.number(),
  isMandatory: z.boolean(),
  isSelected: z.boolean().nullable(),
  position: z.number(),
  notes: z.string().nullish(),
  incrementalQuantity: z.number().nullish(),
});

export type IQuoteItemRespSchema = z.infer<typeof QuoteItemRespSchema>;

export const QuoteItemRespSchemaWithDisplayType = QuoteItemRespSchema.extend({
  quantityDisplayType: ProductQuantityDisplayTypeEnumZ.optional().default(
    ProductQuantityDisplayTypeEnum.INPUT,
  ),
});
export type IQuoteItemWithDisplayType = z.infer<
  typeof QuoteItemRespSchemaWithDisplayType
>;

export enum ApprovalStatusUIEnum {
  SENT = 'SENT',
  SUBMITTED = 'SUBMITTED',
  ACCEPTED = 'ACCEPTED',
  MANUALLY_ACCEPTED = 'MANUALLY_ACCEPTED',
  NOT_REQUIRED = 'NOT_REQUIRED',
  PROCESSED = 'PROCESSED',
}
export const ApprovalStatusUIEnumZ = z.enum(ApprovalStatusUIEnum);

export const QuoteTypeEnumDisplay: {
  [key in QuoteTypeEnum]: string;
} = {
  NEW: 'New',
  AMENDMENT: 'Amendment',
  RENEWAL: 'Renewal',
};

export const NewQuoteTypeEnumDisplay: {
  [key in NewQuoteTypeEnum]: string;
} = {
  NET_NEW: 'Net New',
  MANUAL_RENEWAL: 'Manual Renewal',
  EARLY_RENEWAL: 'Early Renewal',
};

// from GET /api/quotes/{quoteId}:
export const ApprovalRespSchema = BaseResponseSchema.extend({
  ruleId: z.string(),
  userId: z.string().nullish(),
  username: z.string().nullish(),
  teamId: z.string().nullish(),
  teamName: z.string().nullish(),
  name: z.string(),
  description: z.string(),
  rank: z.number(),
  status: ApprovalStatusEnumZ,
  approvalTimestamp: z.string().nullish(),
  approverId: z.string().nullish(),
  approverName: z.string().nullish(),
  declineReason: z.string().nullish(),
  declinerId: z.string().nullish(),
  declinerName: z.string().nullish(),
  declinedTimestamp: z.string().nullish(),
  skipped: z.boolean(),
  quoteId: z.string(),
  mandatory: z.boolean(),
  currentUserApprover: z.boolean(),
  approverOrder: z.number().optional(), // this is optional for backward compatibility, as it was added later and may not be present in older approval records
  notifiedAt: z.string().nullish(), // this is optional for backward compatibility, as it was added later and may not be present in older approval records
});
export type IApprovalRespSchema = z.infer<typeof ApprovalRespSchema>;

const QuoteValidationSchema = z.object({
  errorLevel: RuleValidationErrorLevelEnumZ,
  message: z.string(),
  ruleDescription: z.string(),
  ruleId: z.string(),
  errorLocation: RuleValidationErrorLocationEnumZ.nullish(),
  fields: RuleValidationFieldSchema.array().nullish(),
});
export type IQuoteValidation = z.infer<typeof QuoteValidationSchema>;

export const ApprovalSchemaUI = ApprovalRespSchema.extend({
  status: z.union([ApprovalStatusEnumZ, ApprovalStatusUIEnumZ]),
});
export type IApprovalSchemaUI = z.infer<typeof ApprovalSchemaUI>;

// from GET /api/quotes/{quoteId}:
export const QuoteDiscountRespSchema = BaseResponseSchema.extend({
  name: z.string(),
  description: z.string(),
  discountType: DiscountTypeEnumZ,
  status: DiscountStatusEnumZ,
  discountCode: z.string(),
  quoteId: z.string(),
  discountAmount: z.number(),
  currency: z.string(),
  durationType: DiscountDurationEnumZ,
  durationMonths: z.number(),
  maxUses: z.number(),
  remainingUses: z.number(),
  startDate: z.string(),
  endDate: z.string(),
  autoRemove: z.boolean(),
  accountId: z.string(),
  scopeProductId: z.string(),
  scopeOfferingId: z.string(),
  locked: z.boolean(),
});

export type IQuoteDiscountRespSchema = z.infer<typeof QuoteDiscountRespSchema>;

export const DiscountBreakdownSchema = z.object({
  calculatedCatalogDiscountAmount: z.number(),
  calculatedCustomDiscountAmount: z.number(),
  onetimeDiscountProductAmount: z.number(),
  total: z.number(),
});
export type IDiscountBreakdown = z.infer<typeof DiscountBreakdownSchema>;

export type DiscountBreakdownField = keyof Pick<
  IDiscountBreakdown,
  | 'calculatedCatalogDiscountAmount'
  | 'calculatedCustomDiscountAmount'
  | 'onetimeDiscountProductAmount'
>;

export const QuoteOfferingOptionsSchema = z.object({
  priceDisplay: PriceDisplayEnumZ.nullish(),
});

export const QuoteOfferingDurationSchema = z.object({
  periods: z.number(),
  periodsInMonths: z.number(),
  billingFrequency: RateBillingFrequencyEnumZ,
});

export type IQuoteOfferingDurationSchema = z.infer<
  typeof QuoteOfferingDurationSchema
>;

// from GET /api/quotes/{quoteId}:
export const QuoteOfferingRespSchema = BaseResponseSchema.extend({
  id: z.string(),
  added: z.boolean().nullish(),
  removed: z.boolean().nullish(),
  quoteId: z.string(),
  offeringId: z.string(),
  offeringName: z.string(),
  offeringDescription: z.string().nullable(),
  rateId: z.string(),
  accountSpecificRate: z.boolean(),
  startDate: z.string(),
  endDate: z.string(),
  parentQuoteOfferingId: z.string().nullable(),
  rateName: z.string(),
  rateDescription: z.string().nullable(),
  billingFrequency: RateBillingFrequencyEnumZ,
  billingFrequencyInMonths: z.number().nullable(),
  subscriptionId: z.string().nullable(),
  description: z.string().nullable(),
  debit: z.number(),
  creditProrationMultiplier: z.number(),
  credit: z.number(),
  amountWithoutDiscount: z.number(),
  amount: z.number(),
  discountAmount: z.number(),
  discountPercent: z.number(),
  previousId: z.string().nullable(),
  previousRateId: z.string().nullable(),
  previousAmount: z.number().nullable(),
  items: z.array(QuoteItemRespSchemaWithDisplayType),
  discounts: z.array(QuoteDiscountRespSchema),
  revenueMetrics: RevenueMetricsQuoteItemSchema,
  locked: z.boolean(),
  priceUpliftConfigurationId: z.string().nullable(),
  options: QuoteOfferingOptionsSchema.nullish(),
  position: z.number(),
  subscriptionTiming: z.string().nullable(),
  duration: QuoteOfferingDurationSchema,
  groupId: z.string().nullable(),
});

export type IQuoteOfferingRespSchema = z.infer<typeof QuoteOfferingRespSchema>;

export const QuoteOfferingForUpliftSchema = QuoteOfferingRespSchema.pick({
  id: true,
  quoteId: true,
  startDate: true,
  endDate: true,
  parentQuoteOfferingId: true,
  priceUpliftConfigurationId: true,
  offeringId: true,
  items: true,
  rateId: true,
});
export type QuoteOfferingForUplift = z.infer<
  typeof QuoteOfferingForUpliftSchema
>;

export enum QuoteConditionalTermStatusEnum {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
}

export const QuoteConditionalTermStatusEnumZ = z.enum(
  QuoteConditionalTermStatusEnum,
);

export const QuoteConditionalTermSchema = BaseResponseSchema.extend({
  ruleId: z.string(),
  quoteId: z.string(),
  terms: z.string(),
  displayOrder: z.number(),
  editableOnQuote: z.boolean(),
  ruleName: z.string(),
  status: QuoteConditionalTermStatusEnumZ,
  edited: z.boolean(),
});

export type IQuoteConditionalTermSchema = z.infer<
  typeof QuoteConditionalTermSchema
>;

export const QuoteIncrementalPricesSchema = z.object({
  absolute: z.object({
    amount: z.number(),
    discountAmount: z.number(),
    discountBreakdown: DiscountBreakdownSchema.nullish(),
    amountAfterDiscount: z.number(),
  }),
  prior: z.object({
    amount: z.number(),
    discountAmount: z.number(),
    discountBreakdown: DiscountBreakdownSchema.nullish(),
    amountAfterDiscount: z.number(),
  }),
  bySubscriptionBreakdown: z.any(),
  incremental: z.object({
    amount: z.number(),
    discountAmount: z.number(),
    amountAfterDiscount: z.number(),
  }),
});

// from GET /api/quotes/{quoteId}:
export const QuoteRespSchema = BaseResponseSchema.extend({
  fromCompany: LegalEntitySnapshot,
  acceptedAt: z.string().nullable(),
  processedAt: z.string().nullish(),
  customId: z.string().nullable(),
  type: QuoteTypeEnumZ,
  number: z.string().nullable(),
  description: z.string(),
  opportunity: GetQuoteOpportunitySchema.nullable(),
  accountName: z.string(),
  linkedAccountName: z.string().nullish(),
  orderId: z.string().nullable(),
  owner: z.string(),
  ownerId: z.string().optional(),
  ownerName: z.string(),
  accountId: z.string(),
  linkedAccountId: z.string().nullish(),
  billingAccountId: z.string().nullish(),
  contractId: z.string().nullable(),
  previousQuoteId: z.string().nullable(),
  previousContractId: z.string().nullish(),
  previousContractLength: z.number().nullish(),
  previousNetTerms: z.string().nullish(),
  originalQuoteId: z.string().nullish(),
  contacts: QuoteContacts,
  collaborationAccess: CollaborationAccessEnumZ,
  accessLevel: CollaborationAccessLevelEnumZ.nullable(),
  billGroupId: z.string().nullable(),
  expirationDate: z.string(),
  approvalSubmittedAt: z.string().nullable(),
  approvalGrantedAt: z.string().nullable(),
  credit: z.number().nullable(),
  amount: z.number().nullable(),
  amountWithoutTax: z.number().nullable(),
  discountAmount: z.number().nullable(),
  discountPercent: z.number().nullable(),
  discountBreakdown: DiscountBreakdownSchema.nullish(),
  amountWithoutDiscount: z.number().nullable(),
  /** MSA - If storage */
  documentLocation: z.string().nullable(),
  /** MSA - If link (url) */
  documentUrl: z.string().nullable(),
  /** SOW */
  sowDocumentStorageId: z.string().optional().nullable(),
  esignEnvelopeId: z.string().nullable(),
  currency: z.string(),
  netTerms: NetTermsEnumZ,
  status: QuoteStatusEnumZ,
  cancellationReason: QuoteCancelationReasonEnumZ.nullable(),
  contractStartDate: z.string(),
  contractAmendmentDate: z.string().nullable(),
  contractEndDate: z.string(),
  contractLength: z.number(),
  contractAmount: z.number(),
  contractTerms: z.string().nullable(),
  approvals: z.array(ApprovalRespSchema),
  quoteOfferings: z.array(QuoteOfferingRespSchema),
  noteIds: z.array(z.string()),
  manualAcceptanceReason: z.string().nullable(),
  /** User uploaded signed quote document, or Docusign */
  signedDocumentStorageId: z.string().nullable(),
  revenueMetrics: RevenueMetricsSchema,
  autoRenewContract: z.boolean(),
  requiresEsign: z.boolean(),
  newQuoteType: NewQuoteTypeEnumZ.nullable(),
  sentDate: z.string().nullable(),
  declinedDate: z.string().nullable(),
  purchaseOrderNumber: z.string().max(255).nullish(),
  vatNumber: z.string().optional().nullable(),
  signingOrder: SigningOrderEnumZ,
  validations: z.array(QuoteValidationSchema),
  /** PDf Cover and Footer */
  coverDocumentStorageId: z.string().optional().nullable(),
  footerDocumentStorageId: z.string().optional().nullable(),
  legalEntityId: z.string(),
  customFields: CustomFieldRecordSchema,
  migrated: z.boolean(),
  conditionalTerms: z.array(QuoteConditionalTermSchema).nullable(),
  contractRenewalTerms: QuoteContractRenewalTerms,
  priceUpliftConfigurations: z
    .record(z.string(), PriceUpliftConfigResSchema)
    .nullish(),
  addressSource: QuoteSettingsDefaultAddressSourceEnumZ,
  shippingAddress: AddressSchema.nullish(),
  billingAddress: AddressSchema.nullish(),
  amendmentVersion: QuoteAmendmentVersionEnumZ,
  incrementalPrices: QuoteIncrementalPricesSchema.nullish(),
  startDateSource: QuoteStartDateSourceEnumZ.nullish(),
  autoRenewal: z.boolean().nullish(),
  billingAnchor: ContractAnchorBillingDaySchema.nullable().transform((val) => {
    if (val === null) {
      return { day: null, month: null };
    }
    return val;
  }),
  outputLanguage: z.string().nullish(),
  discountApplicationMethod: DiscountApplicationMethodEnumZ.nullish(),
  metadata: z
    .object({
      bluePrintId: z.string().nullish(),
      sourceBluePrintQuoteId: z.string().nullish(),
      multiAccount: z.boolean().nullish(),
    })
    .nullish(),
  documentConfigurationSummaries: z
    .array(QuoteDocumentConfigSummarySchema)
    .nullable(),
});
export type IQuoteRespSchema = z.infer<typeof QuoteRespSchema>;

export type QuoteWithPdfFields = Partial<
  Pick<
    IQuoteRespSchema,
    | 'id'
    | 'modifyDate'
    | 'description'
    | 'documentLocation'
    | 'sowDocumentStorageId'
    | 'signedDocumentStorageId'
    | 'status'
  >
>;

export const QuoteContactAddressDataSchema = z.object({
  addressSource: QuoteSettingsDefaultAddressSourceEnumZ,
  shippingAddressId: z.string().nullish(),
  billingAddressId: z.string().nullish(),
});
export type IQuoteContactAddressDataInputSchema = z.input<
  typeof QuoteContactAddressDataSchema
>;
export type IQuoteContactAddressDataSchema = z.infer<
  typeof QuoteContactAddressDataSchema
>;

// from GET /api/quotes:
// provides a summary of each quote for use in list views
export const QuoteBasicRespSchema = QuoteRespSchema.pick({
  createdBy: true,
  createDate: true,
  lastModifiedBy: true,
  modifyDate: true,
  id: true,
  description: true,
  type: true,
  status: true,
  amount: true,
  acceptedAt: true,
  contractId: true,
  contractAmount: true,
  contractLength: true,
  contractStartDate: true,
  contractEndDate: true,
  previousContractId: true,
  accountId: true,
  accountName: true,
  opportunity: true,
  ownerId: true,
  ownerName: true,
  billGroupId: true,
  currency: true,
  customFields: true,
  migrated: true,
});
export type IQuoteBasicRespSchema = z.infer<typeof QuoteBasicRespSchema>;

// QuoteOffering Request schema will be used for both PUT/POST since they are identical
// from POST /api/quotes/{quoteId}/quoteOfferings/
// from PUT /api/quotes/{quoteId}/quoteOfferings/{quoteOfferingId}
/**
 * 👉 NOTE: If fields are added or removed that are modified on the quote edit page
 * 👉 you may need to make changes to src/utils/quotes.tsx
 * If field, should not be used to detect a change and make API request on quote page, add to QUOTE_ITEM_FIELDS_COMPARISON_IGNORE
 * If field is complex and request and response have different representation, add an entry to quoteOfferingComparison.item.comparisonFns
 * Otherwise, no action is required
 */
export const QuoteItemReqSchema = z.object({
  id: z.string().nullish(),
  customId: z.string().nullish(),
  productId: z.string(),
  sku: z.string().nullish(),
  description: z
    .string()
    .max(255, 'Product description may not exceed 255 characters')
    .nullish(),
  quantity: z.number({
    message: 'Quantity cannot be empty',
  }),
  incrementalQuantity: z.number().nullish(),
  customDiscountType: DiscountTypeEnumZ.nullish(),
  customDiscountAmountOrPercent: z.union([z.number(), z.string()]).nullable(),
  customDiscountName: z.string().nullish(),
  customDiscountDescription: z.string().nullish(),
  customDiscountExcludeFromRenewal: z.boolean().nullish(),
  customDiscountLifecyclePolicies: z
    .array(DiscountLifeCyclePolicyEnumZ)
    .nullish(),
  customPrice: QuoteItemCustomPriceSchema.nullish(),
  options: QuoteItemOptionsSchema.nullish(),
  isSelected: z.boolean().nullish(),
  position: z.number(),
  notes: z.string().optional().nullable(),
});

export type IQuoteItemReqSchema = z.infer<typeof QuoteItemReqSchema>;
export type IQuoteItemReqSchemaUI = IQuoteItemReqSchema & {
  productType: ProductTypeEnum;
};

export const QuoteOfferingScheduleSchema = z.object({
  startDate: z.string(),
  endDate: z.string().nullish(),
  parentQuoteOfferingId: z.string(),
});

export type IQuoteOfferingScheduleSchema = z.infer<
  typeof QuoteOfferingScheduleSchema
>;

/**
 * 👉 NOTE: If fields are added or removed that are modified on the quote edit page
 * 👉 you may need to make changes to src/utils/quotes.tsx
 * If field, should not be used to detect a change and make API request on quote page, add to QUOTE_OFFERING_FIELDS_COMPARISON_IGNORE
 * If field is complex and request and response have different representation, add an entry to quoteOfferingComparison.offering.comparisonFns
 * Otherwise, no action is required
 */
export const QuoteOfferingReqSchema = z.object({
  offeringId: z.string().nonempty({
    error: 'offeringId is required!',
  }),
  rateId: z.string().nullish(),
  subscriptionId: z.string().nullish(),
  description: z
    .string()
    .max(255, 'Description may not exceed 255 characters')
    .nullish(),
  items: z.array(QuoteItemReqSchema),
  discountIds: z.array(z.string()).nullish(),
  schedule: QuoteOfferingScheduleSchema.nullish(),
  startDate: z.string().nullish(),
  endDate: z.string().nullish(),
  options: QuoteOfferingOptionsSchema.nullish(),
});

// Schema for bulk offering flow - only includes fields needed for the UI
export const BulkOfferingRateSchema = RateResBaseSchema.pick({
  id: true,
  name: true,
  billingFrequencyInMonths: true,
});

export const BulkOfferingProductSchema = z.object({
  product: z.object({
    id: z.string(),
    name: z.string(),
    prorationPolicy: z.enum(ProrationPolicyEnum).nullish(),
    productType: z.enum(ProductTypeEnum).nullish(),
  }),
  isMandatory: z.boolean(),
});

export const BulkOfferingItemSchema = OfferingResSchema.pick({
  id: true,
  name: true,
  type: true,
}).extend({
  offeringProducts: z.array(BulkOfferingProductSchema).optional(),
  rates: z.array(BulkOfferingRateSchema).optional(),
});

export type IBulkOfferingItem = z.infer<typeof BulkOfferingItemSchema>;

export const BulkQuoteOfferingReqSchema = z.object({
  offerings: z.array(BulkOfferingItemSchema),
  billingFrequency: z.string(),
  bulkRate: z.array(
    z.object({
      offeringId: z.string(),
      rateId: z.string().refine((data) => !!data, {
        error: 'Rate ID is required',
      }),
      // Products chosen in the review step; unset means the default selection
      productIds: z.array(z.string()).optional(),
    }),
  ),
  customBillingFrequency: z.number().nullish(),
});

export type IQuoteOfferingReqSchema = z.infer<typeof QuoteOfferingReqSchema>;
export type IBulkQuoteOfferingInputSchema = z.input<
  typeof BulkQuoteOfferingReqSchema
>;
export type IBulkQuoteOfferingReqSchema = z.infer<
  typeof BulkQuoteOfferingReqSchema
>;

export const getQuoteOfferingReqSchema =
  (offeringRate: IRateResSchema | null) =>
  (
    data: IQuoteOfferingReqSchema,
    context: object | undefined,
    options: ResolverOptions<IQuoteOfferingReqSchema>,
  ) => {
    const modifiedReqSchema = QuoteOfferingReqSchema.superRefine(
      ({ items }, ctx) => {
        items.forEach(({ quantity, productId }, index) => {
          const inputName =
            `items.${index}.quantity` as keyof IQuoteOfferingReqSchema;

          const min =
            offeringRate?.productPriceRanges[productId!]?.min &&
            isNumber(offeringRate.productPriceRanges[productId!].min) &&
            !isNaN(offeringRate.productPriceRanges[productId!].min)
              ? offeringRate?.productPriceRanges[productId!].min
              : 0;

          const max =
            offeringRate?.productPriceRanges[productId!]?.max &&
            isNumber(offeringRate.productPriceRanges[productId!].max) &&
            !isNaN(offeringRate.productPriceRanges[productId!].max)
              ? // eslint-disable-next-line @typescript-eslint/no-non-null-assertion, @typescript-eslint/no-non-null-asserted-optional-chain
                offeringRate?.productPriceRanges[productId!].max!
              : Infinity;

          const parsedQuantity =
            isNumber(quantity) && !isNaN(quantity) ? +quantity : 0;

          if (parsedQuantity < min) {
            ctx.addIssue({
              code: 'custom',
              message: `Quantity is lower than set price tiers (min ${min})`,
              path: [inputName],
            });
          }

          if (parsedQuantity > max) {
            ctx.addIssue({
              code: 'custom',
              message: `Quantity is higher than set price tiers (max ${max})`,
              path: [inputName],
            });
          }
        });
      },
    );
    return zodResolver(modifiedReqSchema)(data, context, options);
  };

export interface IQuoteDiscount {
  name: string;
  description: string;
  percentageAmount: number;
  amount: string;
  type: AmountUnitTypeEnum;
  lifeCyclePolicies: DiscountLifeCyclePolicyEnum[];
}

export interface IQuoteDiscounts {
  discounts: IQuoteDiscount[];
  total: number;
}
export const IncrementalPeriodSchema = z.object({
  billDate: z.string(),
  billableAmount: z.number(),
  creditableAmount: z.number(),
});

export const BillingSchedulePeriodsSchema = z.array(
  z.object({
    incrementalLabel: z.string().optional(),
    startDate: z.string(),
    amount: z.number(),
  }),
);

export type IBillingSchedulePeriodsSchema = z.infer<
  typeof BillingSchedulePeriodsSchema
>;

// GET /api/quotes/quote_59Sax3UtRtVw1tY0/billingSchedule
export const QuoteBillingScheduleRespSchema = z.object({
  contractAmount: z.number(),
  billingFrequency: RateBillingFrequencyEnumZ,
  billingFrequencyInMonths: z.number().nullish(),
  periods: BillingSchedulePeriodsSchema,
  incrementalPeriod: IncrementalPeriodSchema.nullish(),
});

export type IQuoteBillingScheduleRespSchema = z.infer<
  typeof QuoteBillingScheduleRespSchema
>;

export type IIncrementalPeriodSchema = z.infer<typeof IncrementalPeriodSchema>;

export interface QuoteOfferingWithChildren {
  quoteOffering: IQuoteOfferingRespSchema;
  children: IQuoteOfferingRespSchema[];
}

export type TQuotePrice = {
  from: number;
  to: number | null;
  amount: number;
  discount: number;
  net: number;
  displayAmount: number;
  displayDiscount: number;
  displayNet: number;
  priceModel: PriceModelEnum;
};

export interface IQuotePriceScheduleProductPrice {
  displayUnitPriceFrequency: number;
  prices: TQuotePrice[];
  product: {
    id: string;
    name: string;
    description: Maybe<string>;
    productType: ProductTypeEnum;
    position: number;
  };
}
export interface IQuotePriceSchedule {
  endDate: string;
  startDate: string;
  productPrices: IQuotePriceScheduleProductPrice[];
  quoteOfferingId: string;
  rate: {
    id: string;
    name: string;
    description: Maybe<string>;
    billingFrequencyInMonths: number;
  };
}
export interface IQuotePrice {
  offering: {
    id: string;
    name: string;
    description: Maybe<string>;
    position: number;
  };
  quoteOfferingId: string;
  schedule: IQuotePriceSchedule[];
}

export const QuoteReviewSubmitSchema = z.object({
  notes: z
    .string()
    .max(2000, 'Approval notes must not exceed 2,000 characters'),
});

export type IQuoteReviewSubmitSchema = z.infer<typeof QuoteReviewSubmitSchema>;

export interface IQuoteReviewSubmit {
  notes: string;
}
export interface IAllowCustomContractRes {
  allowCustomLength: boolean;
}

export enum InactiveItemTypeEnumUI {
  OFFERING = 'OFFERING',
  RATE = 'RATE',
}

export const NewQuoteTypeReqSchema = z
  .object({
    type: NewQuoteTypeEnumZ,
    previousArr: z.union([z.string(), z.number()]).nullish(),
  })
  .refine(
    ({ type, previousArr }) =>
      type === NewQuoteTypeEnum.MANUAL_RENEWAL && !!previousArr,
    {
      message: getRequiredMessage('Previous ARR'),
      path: ['previousArr'],
    },
  );

export type INewQuoteTypeReqSchema = z.infer<typeof NewQuoteTypeReqSchema>;

export const ExtendedExpirationDateReqSchema = z.object({
  extendedDate: z.string().nonempty(getRequiredMessage('Extended Date')),
});

export type IExtendedExpirationDateReqSchema = z.infer<
  typeof ExtendedExpirationDateReqSchema
>;

export type QuoteEditRequest = {
  retainApprovalIds: string[];
};

export type IQuoteReviewReq = {
  note?: string;
};

export const QuoteAddPoNumberSchema = z.object({
  poNumber: z.string().max(40).optional(),
});
export type IQuoteAddPoNumberSchema = z.infer<typeof QuoteAddPoNumberSchema>;

export const IQuoteSigningOrderReq = {
  signingOrder: SigningOrderEnum,
};

export type IQuoteSigningOrderResp = { message: string; status: number };

export const ContactLengthPopoverSchema = z.object({
  contractLength: z.int().nonnegative().nullable(),
});
export type IContactLengthPopoverSchema = z.infer<
  typeof ContactLengthPopoverSchema
>;

export enum QuoteOfferingAmendActionOptionEnum {
  AS_OF = 'AS_OF',
  START_OF_CONTRACT = 'START_OF_CONTRACT',
}
export const QuoteOfferingAmendActionOptionEnumZ = z.enum(
  QuoteOfferingAmendActionOptionEnum,
);
export const QuoteOfferingAmendActionSchema = z.object({
  option: QuoteOfferingAmendActionOptionEnumZ,
  date: z.string().nullish(),
});
export type IQuoteOfferingAmendActionSchema = z.infer<
  typeof QuoteOfferingAmendActionSchema
>;

export enum QuoteOfferingDeleteActionOptionEnum {
  AS_OF = 'AS_OF',
  START_OF_CONTRACT = 'START_OF_CONTRACT',
}
export const QuoteOfferingDeleteActionOptionEnumZ = z.enum(
  QuoteOfferingDeleteActionOptionEnum,
);
export const QuoteOfferingDeleteActionSchema = z.object({
  option: QuoteOfferingDeleteActionOptionEnumZ,
  date: z.string().nullish(),
});

export type IQuoteOfferingDeleteActionSchema = z.infer<
  typeof QuoteOfferingDeleteActionSchema
>;

export interface QuoteOfferingProps {
  index: number;
  quoteOffering?: IQuoteOfferingRespSchema | null;
  parentQuoteOffering?: IQuoteOfferingRespSchema | null;
  /**
   * For scheduled changes, this is the previous scheduled offering or the parent if it is the first scheduled change
   * This is used to know any data points, such as quantity, for the immediate prior offering
   * This will only be set for offerings that are scheduled changes
   */
  priorScheduledQuoteOffering?: IQuoteOfferingRespSchema | null;
  /**
   * For scheduled changes, this is used to calculate valid date ranges for the effective date on amendments
   */
  nextScheduledQuoteOffering?: IQuoteOfferingRespSchema | null;
  childQuoteOfferings?: IQuoteOfferingRespSchema[];
  isChildOffering?: boolean;
  quotePrices: IQuotePrice[];
  isRootOfferingOpen?: boolean;
  isOfferingOpen?: boolean;
  onIsOfferingOpenChange?: (isOpen: boolean) => void;
  /** Rate section open state; a parent offering provides these to control its scheduled-change children */
  isRateOpen?: boolean;
  onIsRateOpenChange?: (isOpen: boolean) => void;
  /** Increment to force the rate sections open, including scheduled-change children */
  expandRatesSignal?: number;
  onChange: OnQuoteOfferingChange;
  quoteOfferings?: QuoteOfferingWithChildren[];
  setParentScheduleChangeActiveProp?: (val: boolean) => void;
}

export type QuoteOfferingState = {
  newlyAddedOffering: boolean;
  isNewQuoteOfferingForm: boolean;
  isLastSegment: boolean;
  isRemoved: boolean;
  isAmendment: boolean;
  isRenewal: boolean;
  isDraft: boolean;
  isLocked: boolean;
  isReadOnlyAndNotEmptyOffering: boolean;
  allowPriceCustomization: boolean;
  gridColumns: string;
  isQuoteOfferingDiscountAvailable: boolean;
  bgColor: string;
  bgColorV2: string;
  isOfferingUnselectable: boolean;
  isOfferingOnetime: boolean;
  isLastSegmentNoChange: boolean;
  isNoOfferingChanged: boolean;
  quoteOfferingGroupStartDate?: string;
  quoteOfferingGroupEndDate?: string;
  isUsingDelayedBilling: boolean;
};

export type EffectiveDateType = number | 'CUSTOM';

export type ScheduleChangeState = {
  availableMonthSelectOptions: {
    title: string;
    value: EffectiveDateType;
  }[];
  monthInterval: number;
  scheduleChangeInitDate: Date;
};

export type UseQuoteOfferingAmendV2Return = {
  isQOUpdated: boolean;
  isQOAddedToAmendment: boolean;
  showRevertToOriginal: boolean;
  isAmendV2Locked: boolean;
  handleRevertOriginal: () => void;
  handleUnlock: () => void;
  isEndingBeforeContractEnd: boolean;
  offeringEndDate: string;
  isOfferingEndBeforeContract: boolean;
};

export enum QuoteOfferingRemovalScopeEnum {
  GROUP = 'group',
  TARGET = 'target',
}
export const QuoteOfferingRemovalScopeEnumZ = z.enum(
  QuoteOfferingRemovalScopeEnum,
);
export enum QuoteOfferingGroupRemovalModeEnum {
  CONTRACT = 'contract',
  TARGET_DATE = 'target_date',
}
export const QuoteOfferingGroupRemovalModeEnumZ = z.enum(
  QuoteOfferingGroupRemovalModeEnum,
);
// https://monetizenow.atlassian.net/browse/BP-9942
export const QuoteOfferingRemoveReqSchema = z.object({
  removal_scope: QuoteOfferingRemovalScopeEnumZ,
  target_id: z.string().nullish(),
  group_removal_mode: QuoteOfferingGroupRemovalModeEnumZ.nullish(),
  group_id: z.string().nullish(),
  end_date: z.string().nullish(),
});
export type IQuoteOfferingRemoveReqSchema = z.infer<
  typeof QuoteOfferingRemoveReqSchema
>;

export type OnQuoteOfferingChangeAction =
  | 'ADD'
  | 'DELETE'
  | 'UPDATE'
  | 'REPLACE';

export type OnQuoteOfferingChange = (
  action: OnQuoteOfferingChangeAction,
  /**
   * One or more quote offerings to work with
   * Delete is the only use-case where there are multiple offerings
   */
  quoteOfferings?: Maybe<IQuoteOfferingRespSchema | IQuoteOfferingRespSchema[]>,
  oldQuoteOfferingId?: string,
  skipFetch?: boolean,
  runEvaluate?: boolean,
) => Promise<void>;

export const ContactLengthPeriodPopoverSchema = z.object({
  contractStartDate: z.string().nullish(),
  contractEndDate: z.string().nullish(),
  lengthInMonths: z.int().nonnegative().nullable(),
  lengthInDays: z.int().nonnegative().nullable(),
  startDateSource: QuoteStartDateSourceEnumZ.nullish(),
  billingAnchor: ContractAnchorBillingDaySchema.nullable(),
});
export type IContactLengthPeriodPopoverSchema = z.infer<
  typeof ContactLengthPeriodPopoverSchema
>;

export interface IQuoteOfferingDeltaViewChangeItem {
  productId: string;
  priceSummary: {
    amount: number;
    discountAmount: number;
    amountAfterDiscount: number;
  };
  quantity: number;
  unitPrice: number;
  unitPriceAfterDiscount: number;
  quoteItemId: string;
  subscriptionItemId: null;
  productName: string;
}
export interface IQuoteOfferingDeltaViewChange {
  type: 'ADDED' | 'REMOVED' | 'UPDATED';
  offeringId: string;
  rateId: string;
  priceSummary: {
    amount: number;
    discountAmount: number;
    amountAfterDiscount: number;
  };
  span: {
    start: string;
    end: string;
  };
  items: IQuoteOfferingDeltaViewChangeItem[];
  quoteOfferingId: string;
  subscriptionId: null;
}

export enum QuoteOfferingViolationLabelEnum {
  // Quote violations offeringId
  DISCONTINUITY_VIOLATION = 'DISCONTINUITY_VIOLATION',
  VARIABLE_QUANTITY_VIOLATION = 'VARIABLE_QUANTITY_VIOLATION',
  // QuoteOffering violations quoteOfferingId
  QUOTE_OFFERING_DESCRIPTION_CHANGE_VIOLATION = 'QUOTE_OFFERING_DESCRIPTION_CHANGE_VIOLATION',
  QUOTE_OFFERING_RATE_CHANGE_VIOLATION = 'QUOTE_OFFERING_RATE_CHANGE_VIOLATION',
  QUOTE_OFFERING_DISCOUNT_CHANGE_VIOLATION = 'QUOTE_OFFERING_DISCOUNT_CHANGE_VIOLATION',
  // QuoteItem violations quoteItemId
  QUOTE_ITEM_DISCOUNT_CHANGE_VIOLATION = 'QUOTE_ITEM_DISCOUNT_CHANGE_VIOLATION',
  QUOTE_ITEM_DESCRIPTION_CHANGE_VIOLATION = 'QUOTE_ITEM_DESCRIPTION_CHANGE_VIOLATION',
  QUOTE_ITEM_CUSTOM_DISCOUNT_CHANGE_VIOLATION = 'QUOTE_ITEM_CUSTOM_DISCOUNT_CHANGE_VIOLATION',
  QUOTE_ITEM_CUSTOM_PRICE_CHANGE_VIOLATION = 'QUOTE_ITEM_CUSTOM_PRICE_CHANGE_VIOLATION',
  // This is only temporary while we wait for BP-11743: [NOT IN MVP SCOPE] Support mandatory optional updates in delta view
  QUOTE_ITEM_MANDATORY_OPTIONAL_CHANGE_VIOLATION = 'QUOTE_ITEM_MANDATORY_OPTIONAL_CHANGE_VIOLATION',
}

export type IQuoteOfferingDeltaViewViolation = {
  label: QuoteOfferingViolationLabelEnum;
  subscriptionId?: string;
  quoteOfferingId?: string;
  quoteItemId?: string;
};

export interface IQuoteOfferingDeltaView {
  contractId: string;
  current: {
    quoteId: string;
    term: {
      start: string;
      end: string;
    };
  };
  previous: {
    quoteId: string;
    term: {
      start: string;
      end: string;
    };
  };
  incrementalPrice: {
    amount: number;
    discountAmount: number;
    amountAfterDiscount: number;
  };
  changes: IQuoteOfferingDeltaViewChange[];
  violations: IQuoteOfferingDeltaViewViolation[];
}

export enum QuoteAcceptanceModeEnum {
  PROCESS_AUTOMATICALLY = 'PROCESS_AUTOMATICALLY',
  PAUSE_AT_ACCEPTED = 'PAUSE_AT_ACCEPTED',
}

export const QuoteShareResponseSchema = z.object({
  quote: QuoteRespSchema.pick({
    id: true,
    description: true,
    accountId: true,
    accountName: true,
    addressSource: true,
    amount: true,
    billGroupId: true,
    contractAmount: true,
    contractStartDate: true,
    contractEndDate: true,
    contractLength: true,
    currency: true,
    expirationDate: true,
    fromCompany: true,
    purchaseOrderNumber: true,
    vatNumber: true,
    status: true,
    type: true,
    requiresEsign: true,
    sentDate: true,
    acceptedAt: true,
  }).extend({
    billingContact: ContactRespSchema,
    billingAddress: AddressSchema,
    shippingContact: ContactRespSchema,
    shippingAddress: AddressSchema,
    externalEsignContact: ContactRespSchema.nullish(),
  }),
  paymentGatewayId: z.string().nullable(),
  paymentMethods: PaymentMethodSummaryResponseSchema.array(),
  allowPoNumber: z.boolean().default(false),
  allowVatNumber: z.boolean().default(false),
  rules: ApplyRecordAutomationRulesResponseSchema.nullish(),
  error: z.string().nullish(),
  redirectUrl: z.string().url().optional().nullable(),
});
export type QuoteShareResponse = z.infer<typeof QuoteShareResponseSchema>;

export const QuoteShareAccessRequestSchema = z.object({
  quoteId: z.string(),
  expiresAt: z.union([z.string(), z.date()]).refine((val) => {
    const date = isDate(val) ? val : parseISO(val);
    return isValidDate(date);
  }, 'Invalid date'),
  redirectUrl: z
    .string()
    .optional()
    .transform((val) =>
      val == null || (typeof val === 'string' && val.trim() === '')
        ? undefined
        : val,
    )
    .pipe(
      z
        .string()
        .url({
          protocol: /^https?$/,
          hostname: z.regexes.domain,
        })
        .optional(),
    ),
});
export type QuoteShareAccessRequest = z.infer<
  typeof QuoteShareAccessRequestSchema
>;

const QuoteWithBillGroup = QuoteBasicRespSchema.extend({
  billGroup: BillGroupSummarySchema.nullish(),
});

export type IQuoteWithBillGroup = z.infer<typeof QuoteWithBillGroup>;

export type CreateDealRoomRequest = {
  customId?: string;
  opportunityId: string;
  quotes: {
    quoteId: string;
    customName?: string;
    position: number;
    note?: string;
  }[];
  expirationDate: string;
  accountId: string;
  requiresEsign: boolean;
};

export const QuoteAddVatNumberSchema = z.object({
  vatNumber: z.string().nullish(),
});
export type QuoteAddVatNumber = z.infer<typeof QuoteAddVatNumberSchema>;

export const QuoteOutputLanguageSchema = z.object({
  outputLanguage: z.string().nullish(),
});

export type QuoteOutputLanguage = z.infer<typeof QuoteOutputLanguageSchema>;

// Quote Blueprint Schemas
export enum BlueprintQuoteScope {
  TENANT = 'TENANT',
  USER = 'USER',
}

export const BlueprintQuoteScopeZ = z.enum(BlueprintQuoteScope);

export const MarkQuoteAsBlueprintRequestSchema = z.object({
  name: z
    .string()
    .min(1, 'Name is required')
    .max(80, 'Name must be 80 characters or less'),
  description: z
    .string()
    .max(1000, 'Description must be 1000 characters or less')
    .nullish(),
  scope: BlueprintQuoteScopeZ.default(BlueprintQuoteScope.TENANT),
});

export type MarkQuoteAsBlueprintRequest = z.infer<
  typeof MarkQuoteAsBlueprintRequestSchema
>;

export const BlueprintQuoteResponseSchema = BaseResponseSchema.extend({
  id: z.string(),
  quote: QuoteBasicRespSchema,
  name: z.string(),
  description: z.string().nullish(),
  isActive: z.boolean(),
  isLocked: z.boolean(),
  quoteCurrency: z.string(),
  scope: BlueprintQuoteScopeZ,
});

export type BlueprintQuoteResponse = z.infer<
  typeof BlueprintQuoteResponseSchema
>;

export const BlueprintQuoteUpdateRequestSchema = z.object({
  name: z
    .string()
    .min(1, 'Name is required')
    .max(80, 'Name must be 80 characters or less'),
  description: z
    .string()
    .max(1000, 'Description must be 1000 characters or less')
    .nullish(),
  isActive: z.boolean(),
  scope: BlueprintQuoteScopeZ.optional(),
});

export type BlueprintQuoteUpdateRequest = z.infer<
  typeof BlueprintQuoteUpdateRequestSchema
>;

// Unified schema for blueprint form (isActive is optional for create, required for update)
export const BlueprintFormRequestSchema = z.object({
  name: z
    .string()
    .min(1, 'Name is required')
    .max(80, 'Name must be 80 characters or less'),
  description: z
    .string()
    .max(1000, 'Description must be 1000 characters or less')
    .nullish(),
  isActive: z.boolean().optional(),
  scope: BlueprintQuoteScopeZ.optional(),
});

export type BlueprintFormRequest = z.infer<typeof BlueprintFormRequestSchema>;

export const ImportDocumentRequestSchema = z.object({
  parent: DocumentParentEnumZ,
  documentConfigId: z.string(),
  position: QuoteDocumentConfigPositionEnumZ,
});

export type ImportDocumentRequest = z.infer<typeof ImportDocumentRequestSchema>;

export type QuoteOfferingGroups = {
  quoteId: string;
  groups: QuoteOfferingGroup[];
};
export enum QuoteOfferingGroupTypeSchema {
  ACCOUNT_HIERARCHY = 'ACCOUNT_HIERARCHY',
}
export const QuoteOfferingGroupTypeSchemaZ = z.enum(
  QuoteOfferingGroupTypeSchema,
);
export const QuoteOfferingGroupSchema = BaseResponseSchema.extend({
  quoteOfferingIds: z.array(z.string()),
  groupType: QuoteOfferingGroupTypeSchemaZ,
  shippingAccount: z.object({
    id: z.string(),
    accountName: z.string(),
  }),
  billingAccount: z.object({
    id: z.string(),
    accountName: z.string(),
  }),
  shippingContactId: z.string(),
  billingContactId: z.string(),
  addressSource: QuoteSettingsDefaultAddressSourceEnumZ,
  shippingAddress: AddressSchema,
  billingAddress: AddressSchema,
  customFields: CustomFieldRecordSchema.nullish(),
  /**
   * The group on the prior quote this one was copied from when an amendment or
   * renewal was created. Set exactly on carried-over groups, so it is what
   * tells a group that already existed at quote creation apart from one added
   * on this quote. Never set on a NEW quote.
   */
  referenceGroupId: z.string().nullish(),
});

export type QuoteOfferingGroup = z.infer<typeof QuoteOfferingGroupSchema>;
export const QuoteOfferingGroupRequestSchema = z.object({
  groupType: QuoteOfferingGroupTypeSchemaZ,
  billingAccountId: z.string().nonempty(getRequiredMessage('Billing Account')),
  shippingAccountId: z
    .string()
    .nonempty(getRequiredMessage('Shipping Account')),
  billingContactId: z.string().nonempty(getRequiredMessage('Billing Contact')),
  shippingContactId: z
    .string()
    .nonempty(getRequiredMessage('Shipping Contact')),
  /**
   * The group's complete membership, root quote offering ids only (child/ramp
   * segments follow their root server-side). There are no dedicated
   * add/remove endpoints — every create/update declares the full list, so
   * omitting an id removes it from the group.
   */
  quoteOfferingIds: z.array(z.string()),
  customFields: CustomFieldRecordSchema.optional(),
});

export type QuoteOfferingGroupRequest = z.infer<
  typeof QuoteOfferingGroupRequestSchema
>;
